<script lang="ts" setup>
import type { Order } from "@shopware-pwa/types";
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import { Divider } from "~/components/clarity-ui/Divider";
import { useOrderSelection } from "~/composables/shop/useOrderSelection";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import OrderLineItem from "~/templates/components/shop/account/OrderLineItem.vue";
import { getCreditLineItems, getLineItemsWithoutCredit } from "~/utils/helper/shop/order";

export interface OrderLineItemsProps {
  order: Order;
  selectable?: boolean;
  showReturns?: boolean;
}

const props = defineProps<OrderLineItemsProps>();

const checkboxes = ref<HTMLInputElement[]>([]);

const getReturnItemById = (itemId: string) =>
  returnedItems.value.find((item) => item.orderLineItemId === itemId);
const lineItems = computed(() => getLineItemsWithoutCredit(props.order?.lineItems));

const creditLineItems = computed(() => getCreditLineItems(props.order?.lineItems));

const returnedItems = computed(
  () => props.order?.extensions?.returns?.flatMap((item) => item.lineItems) ?? [],
);

const returnedLineItems = computed(() => {
  if (creditLineItems.value.length > 0) {
    return creditLineItems.value;
  }

  return lineItems?.value?.filter((item) =>
    returnedItems.value.find((returnItem) => returnItem.orderLineItemId === item.id),
  );
});

const { orderConfiguration } = useOrderSelection();
</script>

<template>
  <div v-if="order" class="flex flex-col gap-2 xl:gap-4">
    <div class="flex flex-col gap-2 xl:gap-4">
      <div class="xl:order-grid hidden w-full text-sm font-medium">
        <div class="col-span-2 xl:mb-2">
          <CommonLabel path="BWF/shop/account/orderHistory/productLabel" />
        </div>
        <div class="xl:text-center">
          <CommonLabel path="BWF/shop/shared/tableQuantity" />
        </div>
        <div class="text-dark justify-self-end">
          <CommonLabel path="BWF/shop/account/orderHistory/total" />
        </div>
        <Divider class="col-span-full hidden xl:block" />
      </div>

      <template
        v-for="lineItem in showReturns ? returnedLineItems : lineItems"
        :key="lineItem.identifier"
      >
        <Checkbox
          v-if="orderConfiguration[lineItem.id]"
          ref="checkboxes"
          v-model="orderConfiguration[lineItem.id].selected"
          :name="lineItem.id"
          class="w-full"
        >
          <OrderLineItem
            :is-configured="true"
            :line-item="lineItem"
            :quantity-change="true"
            class="max-w-full xl:ml-4"
          />
        </Checkbox>
        <OrderLineItem
          v-else
          :line-item="lineItem"
          :return-item="showReturns ? getReturnItemById(lineItem.id) : null"
          class="w-full"
        />
        <Divider class="mb-2 mt-6 last:hidden xl:my-0" />
      </template>
    </div>
  </div>
</template>
